import { useState, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { Popup, Toast } from 'react-vant'
import SkeletonBox from '@/components/SkeletonBox'
import { APIWeb } from '@/api/index'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import Divider from '@/components/Divider'
import { copyToClipboard } from '@/utils'
import Big from 'big.js'
import dayjs from 'dayjs'
import CustomButton from '@/components/CustomButton'
import SelectVerification, { VerActionType } from '@/components/SelectVerification'
import VerificationCodeInput from '@/components/VerificationCodeInput'
import CustomTextInput from '@/components/CustomTextInput'
import { useSendCode } from '@/hooks/useSendCode'
import { useUserInfo } from '@/hooks'
import { parsePaymentSupports } from '../utils'

// CVV敏感信息（不缓存，每次验证后重新获取）
interface CvvSensitiveInfo {
  card_number?: string
  cvv?: string
  expiry_time?: string
}

// 验证步骤
type VerificationStep = 'none' | 'select' | 'verify'

export default function CardInfoPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const cardId = searchParams.get('id') || ''
  const cardIssued = searchParams.get('issued') === 'true'
  useUserInfo() // 保持用户信息

  const { sendCode, sendCodeLoading } = useSendCode()

  const [loading, setLoading] = useState(true)
  const [cardDetail, setCardDetail] = useState<getVirtualCardDetailResponse | null>(null)

  // 获取支付支持
  const paymentSupports = useMemo(() => {
    return parsePaymentSupports(cardDetail?.card?.product?.features)
  }, [cardDetail?.card?.product?.features])

  // CVV敏感信息状态（不缓存，每次验证后清空）
  const [sensitiveInfo, setSensitiveInfo] = useState<CvvSensitiveInfo | null>(null)

  // 验证流程状态
  const [verificationStep, setVerificationStep] = useState<VerificationStep>('none')
  const [verificationPopupVisible, setVerificationPopupVisible] = useState(false)
  const [verificationAccount, setVerificationAccount] = useState('')
  const [verificationType, setVerificationType] = useState<VerActionType | null>(null)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [googleCode, setGoogleCode] = useState('')

  // 获取虚拟卡详情
  useEffect(() => {
    const fetchCardDetail = async () => {
      if (!cardId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const response = await APIWeb.getVirtualCardDetailV2({
          item_id: Number(cardId),
          issued: cardIssued
        })
        setCardDetail(response)
      } catch (error) {
        console.error('获取虚拟卡详情失败:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchCardDetail()
  }, [cardId, cardIssued])

  // 点击查看信息按钮
  const handleViewInfo = useCallback(() => {
    // 清空之前的敏感信息（不缓存）
    setSensitiveInfo(null)
    setGoogleCode('')
    setVerificationStep('select')
    setVerificationPopupVisible(true)
  }, [])

  // 关闭验证弹窗
  const handleCloseVerification = useCallback(() => {
    setVerificationPopupVisible(false)
    setVerificationStep('none')
    setVerificationType(null)
    setVerificationAccount('')
    setGoogleCode('')
  }, [])

  // 选择验证方式成功
  const handleVerificationSelect = useCallback(({ actionType, account }: { actionType: VerActionType; account: string }) => {
    setVerificationType(actionType)
    setVerificationAccount(account)
    setVerificationStep('verify')
  }, [])

  // 重新发送验证码
  const handleResendCode = useCallback(async () => {
    if (!verificationAccount) {
      Toast.fail(t('账户信息缺失'))
      return
    }

    await sendCode({
      account: verificationAccount,
      scene: 'cvv_view',
    })
  }, [verificationAccount, sendCode, t])

  // 处理谷歌验证码输入
  const handleGoogleCodeChange = useCallback((value: string) => {
    setGoogleCode(value)
  }, [])

  // 验证码提交
  const handleVerifySubmit = useCallback(async (code: string) => {
    if (!cardId) return

    setSubmitLoading(true)
    try {
      // 第一步：验证验证码并获取 verify_key
      const isGoogleVerification = verificationType === VerActionType.GoogleVer
      const verifyResult = await APIWeb.verifyOldAccountCode({
        account: isGoogleVerification ? 'google' : verificationAccount,
        scene: 'cvv_view',
        code,
      })

      const verifyKey = verifyResult?.verify_key || code

      // 第二步：调用获取CVV信息接口
      const cvvInfo = await APIWeb.getCardCvvInfoV2({
        card_id: Number(cardId),
        verify_key: verifyKey,
      })

      // 设置敏感信息（不缓存，仅在当前页面显示）
      setSensitiveInfo({
        card_number: cvvInfo?.card_number,
        cvv: cvvInfo?.cvv,
        expiry_time: cvvInfo?.expiry_time,
      })

      Toast.success(t('验证成功'))
      handleCloseVerification()
    } catch (error: any) {
      console.error('验证失败:', error)
    } finally {
      setSubmitLoading(false)
    }
  }, [cardId, verificationType, verificationAccount, handleCloseVerification, t])

  // 谷歌验证器输入组件
  const googleInputVer = useMemo(() => {
    return (
      <div>
        <div className="text-7 font-medium text-black mb-6">
          {t('输入安全验证码')}
        </div>
        <div className="space-y-2">
          <div className="text-sm text-[#667085] mb-2">{t('请输入谷歌验证码')}</div>
          <CustomTextInput
            value={googleCode}
            onChangeText={handleGoogleCodeChange}
            placeholder={t('请输入谷歌验证码')}
          />
        </div>
        <div className="mt-8">
          <CustomButton
            loading={submitLoading}
            disabled={submitLoading || !googleCode || googleCode.length < 6}
            onClick={() => handleVerifySubmit(googleCode)}
          >
            {t('确认')}
          </CustomButton>
        </div>
      </div>
    )
  }, [googleCode, submitLoading, handleGoogleCodeChange, handleVerifySubmit, t])

  // 格式化卡号显示
  const formatCardNumber = (cardNo?: string) => {
    if (!cardNo) return ''
    return cardNo.replace(/(.{4})/g, '$1 ').trim()
  }

  // 格式化有效期显示（使用 dayjs 转换为 MM/YY 格式）
  const formatExpiryTime = (expiry?: string) => {
    if (!expiry) return 'MM/YY'
    // 尝试解析时间戳或日期字符串
    const date = dayjs(expiry)
    if (date.isValid()) {
      return date.format('MM/YY')
    }
    // 如果已经是 MM/YY 格式，直接返回
    if (expiry.includes('/') && expiry.length <= 5) {
      return expiry
    }
    // 如果是 MMYY 格式
    if (expiry.length === 4 && !isNaN(Number(expiry))) {
      return `${expiry.slice(0, 2)}/${expiry.slice(2)}`
    }
    return expiry
  }


  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-base font-medium">{t('卡片信息')}</span>}
      />

      {loading ? (
        <div className="overflow-y-auto px-5 pb-8 pt-5">
          {/* 基本信息骨架屏 */}
          <div className="flex justify-between items-center mb-3">
            <SkeletonBox className="w-20 h-6 rounded" />
            <SkeletonBox className="w-20 h-8 rounded-lg" />
          </div>

          {/* 持卡人、卡号、有效期、安全码骨架屏 */}
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex justify-between items-center py-3">
              <SkeletonBox className="w-16 h-4 rounded" />
              <SkeletonBox className="w-32 h-4 rounded" />
            </div>
          ))}

          {/* 分隔线 */}
          <Divider type="dashed" className="my-4" />

          {/* 卡片属性骨架屏 */}
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="flex justify-between items-center py-3">
              <SkeletonBox className="w-20 h-4 rounded" />
              <SkeletonBox className="w-24 h-4 rounded" />
            </div>
          ))}

          {/* 分隔线 */}
          <Divider type="dashed" className="my-4" />

          {/* 第三方支付支持骨架屏 */}
          <SkeletonBox className="w-28 h-6 rounded mb-4" />
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex justify-between items-center py-2">
              <div className="flex items-center gap-3">
                <SkeletonBox className="w-8.5 h-6 rounded" />
                <SkeletonBox className="w-20 h-4 rounded" />
              </div>
              <SkeletonBox className="w-5 h-5 rounded-full" />
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-y-auto px-5 pb-8 pt-5">
          {/* 基本信息区域 */}
          <div className="flex justify-between items-center mb-3">
            <span className="text-base font-semibold text-black">{t('基础信息')}</span>
            <div>
              {!sensitiveInfo && (
                <CustomButton
                  color="outline"
                  size="small"
                  className="!border-[#D0D5DD] !text-[#344054]"
                  onClick={handleViewInfo}
                >
                  {t('查看信息')}
                </CustomButton>
              )}
            </div>
          </div>

          <div>
            {/* 持卡人 */}
            <div className="flex justify-between items-start py-3">
              <span className="text-sm text-[#667085] shrink-0">{t('持卡人姓名')}</span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-black">
                  {cardDetail?.card?.kyc?.last_name} {cardDetail?.card?.kyc?.first_name}
                </span>
              </div>
            </div>
            {/* 卡号 */}
            <div className="flex justify-between items-start py-3">
              <span className="text-sm text-[#667085] shrink-0">{t('卡号')}</span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-black">
                  {sensitiveInfo?.card_number
                    ? formatCardNumber(sensitiveInfo?.card_number)
                    : (cardDetail?.card?.pan_masked || '')}
                </span>
                {sensitiveInfo?.card_number && (
                  <button
                    className="p-1 border-none bg-transparent cursor-pointer flex items-center justify-center active:opacity-60"
                    onClick={() => copyToClipboard(sensitiveInfo?.card_number || '')}
                  >
                    <img src="/images/card-v2/copy.svg" alt="copy" className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* 有效期 */}
            <div className="flex justify-between items-start py-3">
              <span className="text-sm text-[#667085] shrink-0">{t('有效期')}</span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-black">
                  {formatExpiryTime(sensitiveInfo?.expiry_time)}
                </span>
                {sensitiveInfo?.expiry_time && (
                  <button
                    className="p-1 border-none bg-transparent cursor-pointer flex items-center justify-center active:opacity-60"
                    onClick={() => copyToClipboard(formatExpiryTime(sensitiveInfo?.expiry_time) || '')}
                  >
                    <img src="/images/card-v2/copy.svg" alt="copy" className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* 安全码 */}
            <div className="flex justify-between items-start py-3">
              <span className="text-sm text-[#667085] shrink-0">{t('安全码')}</span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-black">
                  {sensitiveInfo?.cvv || '***'}
                </span>
                {sensitiveInfo?.cvv && (
                  <button
                    className="p-1 border-none bg-transparent cursor-pointer flex items-center justify-center active:opacity-60"
                    onClick={() => copyToClipboard(sensitiveInfo?.cvv || '')}
                  >
                    <img src="/images/card-v2/copy.svg" alt="copy" className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* 分隔线 */}
          {/* <Divider type="dashed" className="my-4" /> */}

          {/* 账单地址区域 */}
          {/* <div>
            <div className="flex justify-between items-start py-3">
              <span className="text-sm text-[#667085] shrink-0">{t('账单地址')}</span>
              <span className="text-sm text-black text-right break-all max-w-60%">{cardDetail?.cpCardAddress}</span>
            </div>
          </div> */}

          {/* 分隔线 */}
          <Divider type="dashed" className="my-4" />

          {/* 卡片属性区域 */}
          <div>
            {/* 卡组织 */}
            <div className="flex justify-between items-start py-3">
              <span className="text-sm text-[#667085] shrink-0">{t('卡组织')}</span>
              <div className="flex items-center gap-2">
                {cardDetail?.card?.product?.payment_network?.toUpperCase() === 'VISA' && (
                  <img src="/images/card-v2/visa.svg" alt="visa" className="w-8 h-5" />
                )}
                {cardDetail?.card?.product?.payment_network?.toUpperCase() === 'MASTERCARD' && (
                  <img src="/images/card-v2/mastercard.svg" alt="mastercard" className="w-8 h-5" />
                )}
                <span className="text-sm text-black">{cardDetail?.card?.product?.payment_network?.toUpperCase()}</span>
              </div>
            </div>

            {/* 卡币种 */}
            <div className="flex justify-between items-start py-3">
              <span className="text-sm text-[#667085] shrink-0">{t('卡币种')}</span>
              <div className="flex items-center gap-2">
                <img src="/icon/usd.svg" alt="usd" className="w-5 h-5" />
                <span className="text-sm text-black">{cardDetail?.card?.product?.currency?.toUpperCase() || 'USD'}</span>
              </div>
            </div>

            {/* 月管理费 */}
            <div className="flex justify-between items-start py-3">
              <span className="text-sm text-[#667085] shrink-0">{t('月管理费')}</span>
              <span className="text-sm text-black">{cardDetail?.card?.product?.monthly_fee || 0} {cardDetail?.card?.product?.currency?.toUpperCase() || 'USD'}</span>
            </div>

            {/* 充值手续费 */}
            <div className="flex justify-between items-start py-3">
              <span className="text-sm text-[#667085] shrink-0">{t('充值手续费')}</span>
              <span className="text-sm text-black">{cardDetail?.card?.product?.deposit_fee || 0} {cardDetail?.card?.product?.currency?.toUpperCase() || 'USD'}</span>
            </div>

            {/* 交易手续费 */}
            <div className="flex justify-between items-start py-3">
              <span className="text-sm text-[#667085] shrink-0">{t('交易手续费')}</span>
              <span className="text-sm text-black">{cardDetail?.card?.product?.bin === '4938' ? '1% - 2%' : new Big(cardDetail?.card?.product?.service_fee_rate || 0).times(100).toFixed(2) + '%'}</span>
            </div>

            {/* 授权费 */}
            <div className="flex justify-between items-start py-3">
              <span className="text-sm text-[#667085] shrink-0">{t('授权费')}</span>
              <span className="text-sm text-black">{cardDetail?.card?.product?.auth_fee || 0} {cardDetail?.card?.product?.currency?.toUpperCase() || 'USD'}</span>
            </div>

            {/* 跨境手续费 */}
            <div className="flex justify-between items-start py-3">
              <span className="text-sm text-[#667085] shrink-0">{t('跨境手续费')}</span>
              <span className="text-sm text-black">{new Big(cardDetail?.card?.product?.exchange_diff_rate || 0).times(100).toFixed(2)}%</span>
            </div>

            {/* 撤销手续费 */}
            <div className="flex justify-between items-start py-3">
              <span className="text-sm text-[#667085] shrink-0">{t('撤销手续费')}</span>
              <span className="text-sm text-black">{cardDetail?.card?.product?.auth_failure_fee || 0} {cardDetail?.card?.product?.currency?.toUpperCase() || 'USD'}</span>
            </div>

            {/* 退款手续费 */}
            <div className="flex justify-between items-start py-3">
              <span className="text-sm text-[#667085] shrink-0">{t('退款手续费')}</span>
              <span className="text-sm text-black">{cardDetail?.card?.product?.auth_failure_fee || 0} {cardDetail?.card?.product?.currency?.toUpperCase() || 'USD'}</span>
            </div>

            {/* 授权失败手续费 */}
            <div className="flex justify-between items-start py-3">
              <span className="text-sm text-[#667085] shrink-0">{t('授权失败手续费')}</span>
              <span className="text-sm text-black">{cardDetail?.card?.product?.auth_failure_fee || 0} {cardDetail?.card?.product?.currency?.toUpperCase() || 'USD'}</span>
            </div>
          </div>

          {/* 分隔线 */}
          <Divider type="dashed" className="my-4" />

          {/* 第三方支付支持 */}
          <div>
            <div className="text-base font-semibold text-black mb-4">{t('第三方支付支持')}</div>

            <div className="flex flex-col gap-3">
              {paymentSupports.map((payment, index) => (
                <div key={index} className="flex justify-between items-center py-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8.5 h-6 border border-[#EAECF0] border-solid rounded-1 flex items-center justify-center">
                      <img src={payment.icon} alt={payment.name} />
                    </div>
                    <span className="text-sm text-black">{payment.name}</span>
                  </div>
                  {payment.supported && (
                    <div className="w-5 h-5">
                      <img src="/images/card-v2/success-status.svg" alt="" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 验证弹窗 */}
      <Popup
        visible={verificationPopupVisible}
        position="bottom"
        round
        closeable
        closeIcon={<img src="/icon/close.svg" alt="" />}
        onClose={handleCloseVerification}
        style={{ height: '70%' }}
      >
        <div className="px-5 pt-8 pb-8 h-full overflow-y-auto">
          {verificationStep === 'select' && (
            <SelectVerification
              scene="cvv_view"
              onSuccess={handleVerificationSelect}
              showPhoneOption={true}
            />
          )}

          {verificationStep === 'verify' && verificationType === VerActionType.GoogleVer && (
            googleInputVer
          )}

          {verificationStep === 'verify' && verificationType !== VerActionType.GoogleVer && (
            <VerificationCodeInput
              account={verificationAccount}
              onSubmit={handleVerifySubmit}
              onResendCode={handleResendCode}
              submitLoading={submitLoading}
              resendLoading={sendCodeLoading}
              submitButtonText={t('确认')}
            />
          )}
        </div>
      </Popup>
    </PageLayout>
  )
}
